/* ════════════════════════════════════════════════════════════════════════
   pw-quote-builder.js
   ────────────────────────────────────────────────────────────────────────
   Quote Builder page logic. Depends on pw-pricing-engine.js (loaded first).

   Boots from a [data-pw-quote-builder] root element. Catalogue URL and
   Worker base URL are read from data attributes set by the section
   template (driven by section settings).

   All eight v1 review fixes are implemented in this file:
     1. Search results — title wrap, no duplicate brand prefix
     2. Type / Brand display — Proper Case
     3. Browse modal — pagination + Brand × Type filtering
     4. Size order — pricing engine sortSizes()
     5. Default to "Exact sizes"
     6. Decoration chips — bigger, prominent active state
     7. Tier price ladder visible per line
     8. Drafts dropped — extractor + runtime safety net
   ════════════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  /* ── BOOT ───────────────────────────────────────────────────────────── */
  var ROOT = document.querySelector('[data-pw-quote-builder]');
  if (!ROOT) return;

  if (!window.pwPricing) {
    console.error('[pw-quote-builder] pw-pricing-engine.js not loaded');
    return;
  }
  var P = window.pwPricing;

  var CATALOGUE_URL = ROOT.dataset.catalogueUrl || '';
  var SWATCH_URL    = ROOT.dataset.swatchUrl || '';
  var WORKER_BASE   = (ROOT.dataset.workerBase || '').replace(/\/+$/, '');

  /* Shopify Files prefix for image-based swatches (tie-dye etc).
     Pattern: /cdn/shop/files/<filename>?width=88
     Hosted on Shopify Files, persists across theme switches. */
  var SHOPIFY_FILES_PREFIX = '/cdn/shop/files/';
  var SwatchMap = {};   /* lowercased colour name → '#hex' or 'filename.png' */

  /* Scoped DOM helper — all selectors live inside ROOT. */
  function $(sel) { return ROOT.querySelector(sel); }
  function $$(sel) { return ROOT.querySelectorAll(sel); }
  function pw(name, ctx) { return (ctx || ROOT).querySelector('[data-pw="' + name + '"]'); }

  /* ── STATE ──────────────────────────────────────────────────────────── */
  var Catalogue = [];
  var ByType = {};
  var ByVendor = {};
  var Lines = [];
  var BrowseFilter = { kind: 'all', value: null, page: 0, pageSize: 60 };

  function uuid() { return Math.random().toString(36).slice(2, 9); }


  /* ════════════════════════════════════════════════════════════════════
     LOAD CATALOGUE
     ════════════════════════════════════════════════════════════════════ */
  if (!CATALOGUE_URL) {
    pw('loading').textContent = 'Catalogue URL not configured. Set it in the section settings.';
    return;
  }

  /* Load catalogue and swatch map in parallel. The swatch map is non-fatal —
     if it fails or isn't configured, we render with neutral fallback colours
     instead of blocking the whole page. */
  var catFetch = fetch(CATALOGUE_URL).then(function (r) {
    if (!r.ok) throw new Error('Catalogue HTTP ' + r.status);
    return r.json();
  });

  var swatchFetch = SWATCH_URL
    ? fetch(SWATCH_URL).then(function (r) { return r.ok ? r.json() : {}; }).catch(function () { return {}; })
    : Promise.resolve({});

  Promise.all([catFetch, swatchFetch])
    .then(function (results) {
      var data = results[0];
      SwatchMap = results[1] || {};

      /* Runtime safety net: drop any product missing pricing data
         even if it sneaks through the extractor. */
      Catalogue = (data || []).filter(function (s) {
        if (s.ap) return s.ap_p && Object.keys(s.ap_p).length > 0;
        if (s.hg) return s.hg_p && s.hg_p.length > 0 && s.do && s.do.length > 0;
        return false;
      });

      Catalogue.forEach(function (s) {
        (ByType[s.y] = ByType[s.y] || []).push(s);
        (ByVendor[s.v] = ByVendor[s.v] || []).push(s);
      });

      populateFilters();
      buildModalSidebar();

      pw('loading').classList.add('pw-hidden');
      pw('empty').classList.remove('pw-hidden');
    })
    .catch(function (err) {
      console.error('[pw-quote-builder] catalogue load failed', err);
      pw('loading').textContent = 'Failed to load catalogue. Check the catalogue URL in section settings.';
    });


  /* ════════════════════════════════════════════════════════════════════
     POPULATE FILTERS — FIX 2: Proper Case, not UPPERCASE
     ════════════════════════════════════════════════════════════════════ */
  function populateFilters() {
    var typeSel = pw('filter-type');
    Object.keys(ByType)
      .filter(Boolean)
      .sort(function (a, b) { return a.toLowerCase().localeCompare(b.toLowerCase()); })
      .forEach(function (t) {
        var opt = document.createElement('option');
        opt.value = t;
        opt.textContent = t + ' (' + ByType[t].length + ')';   /* Proper case */
        typeSel.appendChild(opt);
      });

    var vendorSel = pw('filter-vendor');
    Object.keys(ByVendor)
      .filter(Boolean)
      .sort(function (a, b) { return a.toLowerCase().localeCompare(b.toLowerCase()); })
      .forEach(function (v) {
        var opt = document.createElement('option');
        opt.value = v;
        opt.textContent = v + ' (' + ByVendor[v].length + ')';
        vendorSel.appendChild(opt);
      });
  }


  /* ════════════════════════════════════════════════════════════════════
     SEARCH (autocomplete) — FIX 1: titles wrap, drop duplicate brand prefix
     ════════════════════════════════════════════════════════════════════ */
  function search(query, filters) {
    filters = filters || {};
    var q = (query || '').trim().toLowerCase();
    var tokens = q.split(/\s+/).filter(Boolean);

    var results = [];
    for (var i = 0; i < Catalogue.length; i++) {
      var s = Catalogue[i];
      if (filters.type   && s.y !== filters.type) continue;
      if (filters.vendor && s.v !== filters.vendor) continue;

      if (tokens.length === 0) {
        results.push(s);
        if (results.length >= 200) break;
        continue;
      }

      var hay = (s.t + ' ' + s.v + ' ' + s.y + ' ' + s.i).toLowerCase();
      var allMatch = true;
      for (var j = 0; j < tokens.length; j++) {
        if (hay.indexOf(tokens[j]) === -1) { allMatch = false; break; }
      }
      if (allMatch) {
        results.push(s);
        if (results.length >= 200) break;
      }
    }
    return results;
  }

  /**
   * Strip the vendor prefix from the title if it's repeated below.
   * "AS Colour Staple Tee 5001" with vendor "AS Colour" → "Staple Tee 5001"
   */
  function titleWithoutVendor(title, vendor) {
    if (!vendor || !title) return title;
    var lowerT = title.toLowerCase();
    var lowerV = vendor.toLowerCase();
    if (lowerT.indexOf(lowerV) === 0) {
      return title.substring(vendor.length).trim();
    }
    return title;
  }

  function renderSearchResults(query) {
    var typeFilter = pw('filter-type').value;
    var vendorFilter = pw('filter-vendor').value;
    var results = search(query, { type: typeFilter, vendor: vendorFilter });

    var box = pw('search-results');
    if (!query.trim() && !typeFilter && !vendorFilter) {
      box.classList.add('is-hidden');
      return;
    }

    box.classList.remove('is-hidden');
    if (results.length === 0) {
      box.innerHTML = '<div class="pw-search-empty">No products match your search.</div>';
      return;
    }

    /* Group by type — limit total displayed. */
    var groups = {};
    results.slice(0, 80).forEach(function (s) {
      (groups[s.y] = groups[s.y] || []).push(s);
    });

    var html = '';
    Object.keys(groups).forEach(function (type) {
      html += '<div class="pw-result-group-label">' + escapeHtml(type) + ' · <strong>' + groups[type].length + '</strong></div>';
      groups[type].forEach(function (s) {
        var titleClean = titleWithoutVendor(s.t, s.v);
        var img = s.img
          ? '<img src="' + escapeAttr(s.img) + '" alt="" loading="lazy" />'
          : 'IMG';
        var tag = s.ap
          ? '<span class="pw-result-tag is-apparel">Apparel</span>'
          : '<span class="pw-result-tag is-hg">Hard goods</span>';
        var meta = escapeHtml(s.v);
        if (s.cw && s.cw.length) {
          meta += ' · ' + s.cw.length + ' colour' + (s.cw.length > 1 ? 's' : '');
        }
        html += '<div class="pw-result" data-id="' + escapeAttr(s.i) + '" tabindex="0" role="option">'
              + '  <div class="pw-result-thumb">' + img + '</div>'
              + '  <div class="pw-result-info">'
              + '    <div class="pw-result-title">' + escapeHtml(titleClean) + '</div>'
              + '    <div class="pw-result-meta">' + meta + '</div>'
              + '  </div>'
              + '  ' + tag
              + '</div>';
      });
    });
    if (results.length > 80) {
      html += '<div class="pw-search-empty">+ ' + (results.length - 80) + ' more — narrow your search</div>';
    }
    box.innerHTML = html;

    box.querySelectorAll('.pw-result').forEach(function (el) {
      el.addEventListener('click', function () {
        addLineByStyleId(el.dataset.id);
        pw('search-input').value = '';
        box.classList.add('is-hidden');
      });
    });
  }

  pw('search-input').addEventListener('input', function (e) {
    renderSearchResults(e.target.value);
  });
  pw('search-input').addEventListener('focus', function (e) {
    if (e.target.value.trim() || pw('filter-type').value || pw('filter-vendor').value) {
      renderSearchResults(e.target.value);
    }
  });
  document.addEventListener('click', function (e) {
    if (!e.target.closest('.pw-search-wrap')) {
      pw('search-results').classList.add('is-hidden');
    }
  });
  pw('filter-type').addEventListener('change', function () {
    renderSearchResults(pw('search-input').value);
  });
  pw('filter-vendor').addEventListener('change', function () {
    renderSearchResults(pw('search-input').value);
  });

  $$('.pw-picker-quick-chip').forEach(function (chip) {
    chip.addEventListener('click', function () {
      pw('filter-type').value = chip.dataset.quickfilter;
      pw('filter-vendor').value = '';
      pw('search-input').value = '';
      pw('search-input').focus();
      renderSearchResults('');
    });
  });


  /* ════════════════════════════════════════════════════════════════════
     BROWSE MODAL — FIX 3: pagination + Brand × Type filtering
     ════════════════════════════════════════════════════════════════════ */
  function buildModalSidebar() {
    var sb = pw('modal-sidebar');
    var typesSorted = Object.keys(ByType).filter(Boolean)
      .sort(function (a, b) { return a.toLowerCase().localeCompare(b.toLowerCase()); });
    var vendorsSorted = Object.keys(ByVendor).filter(Boolean)
      .sort(function (a, b) { return a.toLowerCase().localeCompare(b.toLowerCase()); });

    sb.innerHTML = ''
      + '<div class="pw-modal-section">'
      +   '<div class="pw-modal-section-label">Categories</div>'
      +   '<button type="button" class="pw-modal-section-item is-active" data-filter-kind="all">'
      +     '<span>All products</span><span class="pw-modal-section-item-count">' + Catalogue.length + '</span>'
      +   '</button>'
      +   typesSorted.map(function (t) {
            return '<button type="button" class="pw-modal-section-item" data-filter-kind="type" data-filter-value="' + escapeAttr(t) + '">'
                 + '<span>' + escapeHtml(t) + '</span><span class="pw-modal-section-item-count">' + ByType[t].length + '</span>'
                 + '</button>';
          }).join('')
      + '</div>'
      + '<div class="pw-modal-section">'
      +   '<div class="pw-modal-section-label">Brands</div>'
      +   vendorsSorted.map(function (v) {
            return '<button type="button" class="pw-modal-section-item" data-filter-kind="vendor" data-filter-value="' + escapeAttr(v) + '">'
                 + '<span>' + escapeHtml(v) + '</span><span class="pw-modal-section-item-count">' + ByVendor[v].length + '</span>'
                 + '</button>';
          }).join('')
      + '</div>';

    sb.querySelectorAll('.pw-modal-section-item').forEach(function (el) {
      el.addEventListener('click', function () {
        sb.querySelectorAll('.pw-modal-section-item').forEach(function (e) { e.classList.remove('is-active'); });
        el.classList.add('is-active');
        BrowseFilter.kind = el.dataset.filterKind;
        BrowseFilter.value = el.dataset.filterValue || null;
        BrowseFilter.page = 0;
        renderModalResults();
      });
    });

    renderModalResults();
  }

  function renderModalResults() {
    var pool;
    if (BrowseFilter.kind === 'type')   pool = ByType[BrowseFilter.value] || [];
    else if (BrowseFilter.kind === 'vendor') pool = ByVendor[BrowseFilter.value] || [];
    else pool = Catalogue;

    var totalPages = Math.max(1, Math.ceil(pool.length / BrowseFilter.pageSize));
    if (BrowseFilter.page >= totalPages) BrowseFilter.page = totalPages - 1;
    var start = BrowseFilter.page * BrowseFilter.pageSize;
    var shown = pool.slice(start, start + BrowseFilter.pageSize);

    /* Grid */
    var html = '<div class="pw-modal-results-grid">';
    shown.forEach(function (s) {
      var titleClean = titleWithoutVendor(s.t, s.v);
      html += '<button type="button" class="pw-modal-card" data-id="' + escapeAttr(s.i) + '">'
            + '  <div class="pw-modal-card-thumb">' + (s.img ? '<img src="' + escapeAttr(s.img) + '" alt="" loading="lazy" />' : '') + '</div>'
            + '  <div class="pw-modal-card-title">' + escapeHtml(titleClean) + '</div>'
            + '  <div class="pw-modal-card-meta">' + escapeHtml(s.v) + ' · ' + escapeHtml(s.y) + '</div>'
            + '</button>';
    });
    html += '</div>';
    pw('modal-results').innerHTML = html;

    /* Pagination */
    var rangeStart = pool.length === 0 ? 0 : start + 1;
    var rangeEnd = start + shown.length;
    var pagHTML = ''
      + '<button type="button" data-pag="prev"' + (BrowseFilter.page === 0 ? ' disabled' : '') + '>← Prev</button>'
      + '<span>Showing <strong>' + rangeStart + '–' + rangeEnd + '</strong> of <strong>' + pool.length + '</strong>'
      + ' · Page ' + (BrowseFilter.page + 1) + ' of ' + totalPages + '</span>'
      + '<button type="button" data-pag="next"' + (BrowseFilter.page >= totalPages - 1 ? ' disabled' : '') + '>Next →</button>';
    pw('modal-pagination').innerHTML = pagHTML;

    /* Wire up cards */
    pw('modal-results').querySelectorAll('.pw-modal-card').forEach(function (el) {
      el.addEventListener('click', function () {
        addLineByStyleId(el.dataset.id);
        pw('browse-modal').classList.add('is-hidden');
      });
    });

    /* Wire up pagination */
    var prev = pw('modal-pagination').querySelector('[data-pag="prev"]');
    var next = pw('modal-pagination').querySelector('[data-pag="next"]');
    if (prev) prev.addEventListener('click', function () {
      if (BrowseFilter.page > 0) { BrowseFilter.page--; renderModalResults(); pw('modal-results').scrollTop = 0; }
    });
    if (next) next.addEventListener('click', function () {
      if (BrowseFilter.page < totalPages - 1) { BrowseFilter.page++; renderModalResults(); pw('modal-results').scrollTop = 0; }
    });
  }

  pw('open-browse').addEventListener('click', function () {
    pw('browse-modal').classList.remove('is-hidden');
  });
  pw('modal-close').addEventListener('click', function () {
    pw('browse-modal').classList.add('is-hidden');
  });
  pw('browse-modal').addEventListener('click', function (e) {
    if (e.target === pw('browse-modal')) pw('browse-modal').classList.add('is-hidden');
  });


  /* ════════════════════════════════════════════════════════════════════
     ADD LINE — FIX 5: defaults to "Exact sizes" mode
     ════════════════════════════════════════════════════════════════════ */
  function addLineByStyleId(id) {
    var style = Catalogue.find(function (s) { return s.i === id; });
    if (!style) { showToast('Product not found'); return; }

    var line = {
      id: uuid(),
      style_id: id,
      style: style,
      colour: (style.cw && style.cw[0]) || ''
    };

    if (style.ap) {
      line.kind = 'apparel';
      line.size_mode = 'hard';     /* FIX 5: default to exact sizes */
      line.size_breakdown = {};
      line.soft_qty = 50;
      line.artwork = { front: null, back: null };
      line.art_active_side = 'front';

      var availableMethods = (style.em || []).filter(function (m) {
        return P.methodLabelToKey(m);
      });
      var defaultMethod = availableMethods[0] || 'Embroidery';

      line.deco = {
        method:     defaultMethod,
        colours:    1,
        repeat:     false,
        front_back: 'front',
        underbase:  P.needsUnderbase(line.colour)
      };
    } else {
      line.kind = 'hg';
      line.artwork = null;
      line.qty = (style.moq) || (style.hg_p && style.hg_p[0] ? style.hg_p[0].qty : 100);
      var dos = (style.do || []).filter(function (d) { return d.type === 'DO'; });
      line.deco_idx = 0;
      line.deco_method = dos.length > 0 ? dos[0].method : '';
    }

    Lines.push(line);
    renderLines();
    showToast('Added ' + titleWithoutVendor(style.t, style.v));
  }


  /* ════════════════════════════════════════════════════════════════════
     RENDER LINES
     ════════════════════════════════════════════════════════════════════ */
  function renderLines() {
    var container = pw('lines-container');
    container.innerHTML = '';

    var hasLines = Lines.length > 0;
    pw('empty').classList.toggle('pw-hidden', hasLines);
    pw('add-line').classList.toggle('pw-hidden', !hasLines);

    Lines.forEach(function (line, idx) {
      var el = (line.kind === 'apparel')
        ? renderApparel(line, idx)
        : renderHardGoods(line, idx);
      container.appendChild(el);
    });

    updateTotals();
  }


  /* ── APPAREL LINE ──────────────────────────────────────────────────── */
  function renderApparel(line, idx) {
    var s = line.style;
    var div = document.createElement('div');
    div.className = 'pw-line pw-fade-in';
    div.dataset.lineId = line.id;
    div.style.animationDelay = (idx * 50) + 'ms';

    var thumbImg = (line.colour && s.ci && s.ci[line.colour]) || s.img;
    var thumb = thumbImg ? '<img src="' + escapeAttr(thumbImg) + '" alt="" />' : 'IMG';

    /* Methods supported by the pricing engine */
    var supportedMethods = (s.em || []).filter(function (m) { return P.methodLabelToKey(m); });
    if (supportedMethods.length === 0) supportedMethods = ['Embroidery'];

    /* FIX 6: bigger decoration chips with stronger active state — see CSS */
    var methodChips = supportedMethods.map(function (m) {
      var active = (m === line.deco.method) ? ' is-active' : '';
      var meta = methodMetaApparel(m);
      return '<button type="button" class="pw-method-chip' + active + '" data-method="' + escapeAttr(m) + '">'
           +    escapeHtml(m)
           +    '<span class="pw-method-chip-meta">' + meta + '</span>'
           + '</button>';
    }).join('');

    /* Colour swatches — PDP-style grid with optional search */
    var colourSwatches = '';
    if (s.cw && s.cw.length > 1) {
      colourSwatches = renderSwatchGrid(s.cw, line.colour, line.id);
    } else if (s.cw && s.cw.length === 1) {
      var swatch = lookupSwatch(s.cw[0]);
      var dotStyle;
      var lightCls = '';
      if (!swatch) {
        dotStyle = 'background:#cccccc;';
      } else if (swatch.kind === 'image') {
        dotStyle = 'background-image:url(' + escapeAttr(swatch.value) + ');background-size:cover;background-position:center;';
      } else {
        dotStyle = 'background:' + swatch.value + ';';
        if (swatch.isLight) lightCls = ' is-light';
      }
      colourSwatches = '<span class="pw-swatch-single">'
                     + '<span class="pw-swatch-dot' + lightCls + '" style="' + dotStyle + '"></span>'
                     + escapeHtml(s.cw[0]) + ' (only colour)'
                     + '</span>';
    }

    /* FIX 4: sizes via pricing engine sortSizes() */
    var sizes = P.sortSizes(s.sz || []);
    if (sizes.length === 0) sizes = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'];
    if (sizes.length === 1 && sizes[0] === 'One Size') sizes = ['One Size'];

    var deco = line.deco;
    var key = P.methodLabelToKey(deco.method);
    var isScreenPrint = (key === 'screen-printing');
    var isDtgDtf = (key === 'dtg' || key === 'dtf');
    var isEmb = (key === 'embroidery');

    var titleClean = titleWithoutVendor(s.t, s.v);

    div.innerHTML = ''
      + '<div class="pw-line-header">'
      +   '<div class="pw-line-thumb">' + thumb + '</div>'
      +   '<div class="pw-line-info">'
      +     '<div class="pw-line-eyebrow">'
      +       '<span>Item ' + (idx + 1) + '</span>'
      +       '<span class="pw-line-eyebrow-divider"></span>'
      +       '<span>' + escapeHtml(s.y) + ' · ' + escapeHtml(s.v) + '</span>'
      +       '<span class="pw-line-eyebrow-divider"></span>'
      +       '<strong>Apparel</strong>'
      +     '</div>'
      +     '<h3 class="pw-line-title">' + escapeHtml(titleClean) + '</h3>'
      +     '<span class="pw-line-title-meta">'
      +        (s.fw ? escapeHtml(s.fw) : '')
      +        (s.cw && s.cw.length > 1 ? ' · ' + s.cw.length + ' colours' : '')
      +     '</span>'
      +   '</div>'
      +   '<button type="button" class="pw-line-remove" aria-label="Remove">×</button>'
      + '</div>'
      + '<div class="pw-line-body">'
      +   '<div class="pw-config-grid">'
      +     '<div class="pw-config-block">'
      +       '<div class="pw-config-label">Colour</div>'
      +       '<div class="pw-swatches">' + colourSwatches + '</div>'
      +     '</div>'
      +     '<div class="pw-config-block">'
      +       '<div class="pw-config-label">Decoration</div>'
      +       '<div class="pw-method-chips">' + methodChips + '</div>'
      +       '<div class="pw-toggle-row" style="margin-top:8px;">'
      +         (isScreenPrint
                  ? '<label><span>Colours:</span> <select data-control="colours" class="pw-mini-select">'
                    + '<option value="1"' + (deco.colours === 1 ? ' selected' : '') + '>1</option>'
                    + '<option value="2"' + (deco.colours === 2 ? ' selected' : '') + '>2</option>'
                    + '<option value="3"' + (deco.colours === 3 ? ' selected' : '') + '>3</option>'
                    + '<option value="4"' + (deco.colours >= 4 ? ' selected' : '') + '>4+</option>'
                    + '</select></label>'
                  : '')
      +         (!isEmb
                  ? '<label><input type="checkbox" data-control="frontback"' + (deco.front_back === 'front+back' ? ' checked' : '') + ' /> Front + back</label>'
                  : '')
      +         (isScreenPrint || isEmb
                  ? '<label><input type="checkbox" data-control="repeat"' + (deco.repeat ? ' checked' : '') + ' /> Repeat order</label>'
                  : '')
      +       '</div>'
      +     '</div>'
      +   '</div>'
      +   '<div class="pw-config-block">'
      +     '<div class="pw-config-label">Quantity by size</div>'
      +     '<div style="display:flex;gap:8px;align-items:center;margin-bottom:6px;">'
      +       '<div class="pw-mode-toggle">'
      +         '<button type="button" class="' + (line.size_mode === 'hard' ? 'is-active' : '') + '" data-mode="hard">Exact sizes</button>'
      +         '<button type="button" class="' + (line.size_mode === 'soft' ? 'is-active' : '') + '" data-mode="soft">Estimate (lock later)</button>'
      +       '</div>'
      +     '</div>'
      +     '<div class="pw-size-section" ' + (line.size_mode === 'soft' ? 'style="display:none"' : '') + '>'
      +       '<div class="pw-size-grid">'
      +         sizes.map(function (sz) {
                  var v = (line.size_breakdown && line.size_breakdown[sz]) || 0;
                  return '<div class="pw-size-cell">'
                       +   '<div class="pw-size-cell-label">' + escapeHtml(sz) + '</div>'
                       +   '<input type="number" min="0" data-size="' + escapeAttr(sz) + '" value="' + v + '" />'
                       + '</div>';
                }).join('')
      +       '</div>'
      +     '</div>'
      +     '<div class="pw-soft-section" ' + (line.size_mode === 'hard' ? 'style="display:none"' : '') + '>'
      +       '<input class="pw-soft-input" type="number" min="1" data-control="softqty" value="' + (line.soft_qty || 50) + '" />'
      +       '<div class="pw-deco-meta" style="margin-top:6px;">We\'ll send your team a size-collection form before production.</div>'
      +     '</div>'
      +   '</div>'
      +   '<div class="pw-config-block" style="margin-top:14px;">'
      +     '<div class="pw-config-label">Tier pricing</div>'
      +     '<div class="pw-tier-ladder" data-tier-ladder></div>'
      +   '</div>'
      +   '<div class="pw-config-block pw-config-block--artwork" style="margin-top:14px;">'
      +     '<div class="pw-art-section-header">Add Your Logo or Artwork</div>'
      +     renderArtworkSection(line)
      +   '</div>'
      +   '<div class="pw-line-subtotal" data-subtotal></div>'
      + '</div>';

    wireApparelHandlers(div, line);
    recalcAndRenderApparel(div, line);
    return div;
  }


  /* ── HARD GOODS LINE ──────────────────────────────────────────────── */
  function renderHardGoods(line, idx) {
    var s = line.style;
    var div = document.createElement('div');
    div.className = 'pw-line is-hg pw-fade-in';
    div.dataset.lineId = line.id;
    div.style.animationDelay = (idx * 50) + 'ms';

    var thumbImg = (line.colour && s.ci && s.ci[line.colour]) || s.img;
    var thumb = thumbImg ? '<img src="' + escapeAttr(thumbImg) + '" alt="" />' : 'IMG';

    var dos = (s.do || []).filter(function (d) { return d.type === 'DO'; });

    /* FIX 6: bigger decoration chips */
    var methodChips = dos.map(function (d, i) {
      var active = (i === line.deco_idx) ? ' is-active' : '';
      return '<button type="button" class="pw-method-chip' + active + '" data-deco-idx="' + i + '">'
           +    escapeHtml(d.method)
           +    '<span class="pw-method-chip-meta">' + escapeHtml((d.area || '').slice(0, 60)) + '</span>'
           + '</button>';
    }).join('');

    var colourSwatches = '';
    if (s.cw && s.cw.length > 1) {
      colourSwatches = renderSwatchGrid(s.cw, line.colour, line.id);
    } else if (s.cw && s.cw.length === 1) {
      var swatch = lookupSwatch(s.cw[0]);
      var dotStyle;
      var lightCls = '';
      if (!swatch) {
        dotStyle = 'background:#cccccc;';
      } else if (swatch.kind === 'image') {
        dotStyle = 'background-image:url(' + escapeAttr(swatch.value) + ');background-size:cover;background-position:center;';
      } else {
        dotStyle = 'background:' + swatch.value + ';';
        if (swatch.isLight) lightCls = ' is-light';
      }
      colourSwatches = '<span class="pw-swatch-single">'
                     + '<span class="pw-swatch-dot' + lightCls + '" style="' + dotStyle + '"></span>'
                     + escapeHtml(s.cw[0])
                     + '</span>';
    } else {
      colourSwatches = '<span style="font-size:11px;color:var(--pw-mid);">No colour variants</span>';
    }

    var titleClean = titleWithoutVendor(s.t, s.v);

    div.innerHTML = ''
      + '<div class="pw-line-header">'
      +   '<div class="pw-line-thumb">' + thumb + '</div>'
      +   '<div class="pw-line-info">'
      +     '<div class="pw-line-eyebrow">'
      +       '<span>Item ' + (idx + 1) + '</span>'
      +       '<span class="pw-line-eyebrow-divider"></span>'
      +       '<span>' + escapeHtml(s.y) + ' · ' + escapeHtml(s.v) + '</span>'
      +       '<span class="pw-line-eyebrow-divider"></span>'
      +       '<strong style="color:var(--pw-red);">Hard goods</strong>'
      +     '</div>'
      +     '<h3 class="pw-line-title">' + escapeHtml(titleClean) + '</h3>'
      +     '<span class="pw-line-title-meta">'
      +        (s.vm ? escapeHtml(s.vm) + 'mL · ' : '')
      +        (s.cw && s.cw.length > 1 ? s.cw.length + ' colours' : '')
      +     '</span>'
      +   '</div>'
      +   '<button type="button" class="pw-line-remove" aria-label="Remove">×</button>'
      + '</div>'
      + '<div class="pw-line-body">'
      +   '<div class="pw-config-grid">'
      +     '<div class="pw-config-block">'
      +       '<div class="pw-config-label">Colour</div>'
      +       '<div class="pw-swatches">' + colourSwatches + '</div>'
      +     '</div>'
      +     '<div class="pw-config-block">'
      +       '<div class="pw-config-label">Quantity</div>'
      +       '<div class="pw-qty-stepper">'
      +         '<button type="button" data-qty="-50">−</button>'
      +         '<input type="number" min="1" data-control="qty" value="' + line.qty + '" />'
      +         '<button type="button" data-qty="+50">+</button>'
      +       '</div>'
      +       '<div class="pw-deco-meta">MOQ: ' + (s.moq || (s.hg_p && s.hg_p[0] ? s.hg_p[0].qty : 0)) + ' units</div>'
      +     '</div>'
      +   '</div>'
      +   '<div class="pw-config-block" style="margin-top:14px;">'
      +     '<div class="pw-config-label">Decoration method</div>'
      +     (dos.length > 0
          ? '<div class="pw-method-chips">' + methodChips + '</div>'
          : '<div class="pw-deco-meta">No decoration options on this product</div>')
      +   '</div>'
      +   '<div class="pw-config-block" style="margin-top:14px;">'
      +     '<div class="pw-config-label">Tier pricing</div>'
      +     '<div class="pw-tier-ladder" data-tier-ladder></div>'
      +   '</div>'
      +   '<div class="pw-config-block" style="margin-top:14px;">'
      +     '<div class="pw-config-label">Artwork</div>'
      +     renderArtworkUpload(line)
      +   '</div>'
      +   '<div class="pw-line-subtotal" data-subtotal></div>'
      + '</div>';

    wireHardGoodsHandlers(div, line);
    recalcAndRenderHardGoods(div, line);
    return div;
  }


  /* ════════════════════════════════════════════════════════════════════
     HANDLERS
     ════════════════════════════════════════════════════════════════════ */
  function wireApparelHandlers(div, line) {
    div.querySelector('.pw-line-remove').addEventListener('click', function () { removeLine(line.id); });

    div.querySelectorAll('.pw-swatch[data-colour]').forEach(function (sw) {
      sw.addEventListener('click', function () {
        div.querySelectorAll('.pw-swatch').forEach(function (e) { e.classList.remove('is-active'); });
        sw.classList.add('is-active');
        line.colour = sw.dataset.colour;
        line.deco.underbase = P.needsUnderbase(line.colour);
        var newImg = (line.style.ci && line.style.ci[line.colour]) || line.style.img;
        if (newImg) div.querySelector('.pw-line-thumb').innerHTML = '<img src="' + escapeAttr(newImg) + '" alt="" />';
        recalcAndRenderApparel(div, line);
        updateTotals();
      });
    });
    wireSwatchSearch(div);

    div.querySelectorAll('.pw-method-chip[data-method]').forEach(function (c) {
      c.addEventListener('click', function () {
        div.querySelectorAll('.pw-method-chip').forEach(function (e) { e.classList.remove('is-active'); });
        c.classList.add('is-active');
        line.deco.method = c.dataset.method;
        var idx = Lines.indexOf(line);
        var newDiv = renderApparel(line, idx);
        div.parentNode.replaceChild(newDiv, div);
        updateTotals();
      });
    });

    var coloursControl = div.querySelector('[data-control="colours"]');
    if (coloursControl) coloursControl.addEventListener('change', function (e) {
      line.deco.colours = parseInt(e.target.value, 10);
      recalcAndRenderApparel(div, line);
      updateTotals();
    });
    var fbControl = div.querySelector('[data-control="frontback"]');
    if (fbControl) fbControl.addEventListener('change', function (e) {
      line.deco.front_back = e.target.checked ? 'front+back' : 'front';
      /* Sides changed → artwork tabs must update (Back tab appears/disappears). */
      refreshArtworkSection(div, line);
      recalcAndRenderApparel(div, line);
      updateTotals();
    });
    var repeatControl = div.querySelector('[data-control="repeat"]');
    if (repeatControl) repeatControl.addEventListener('change', function (e) {
      line.deco.repeat = e.target.checked;
      recalcAndRenderApparel(div, line);
      updateTotals();
    });

    div.querySelectorAll('[data-mode]').forEach(function (b) {
      b.addEventListener('click', function () {
        line.size_mode = b.dataset.mode;
        div.querySelectorAll('[data-mode]').forEach(function (e) { e.classList.remove('is-active'); });
        b.classList.add('is-active');
        div.querySelector('.pw-size-section').style.display = (line.size_mode === 'soft') ? 'none' : '';
        div.querySelector('.pw-soft-section').style.display = (line.size_mode === 'hard') ? 'none' : '';
        recalcAndRenderApparel(div, line);
        updateTotals();
      });
    });

    div.querySelectorAll('[data-size]').forEach(function (inp) {
      inp.addEventListener('input', function () {
        line.size_breakdown[inp.dataset.size] = Math.max(0, parseInt(inp.value, 10) || 0);
        recalcAndRenderApparel(div, line);
        updateTotals();
      });
    });

    var softInp = div.querySelector('[data-control="softqty"]');
    if (softInp) softInp.addEventListener('input', function (e) {
      line.soft_qty = Math.max(1, parseInt(e.target.value, 10) || 1);
      recalcAndRenderApparel(div, line);
      updateTotals();
    });

    wireArtworkSection(div, line);
  }

  function wireHardGoodsHandlers(div, line) {
    div.querySelector('.pw-line-remove').addEventListener('click', function () { removeLine(line.id); });

    div.querySelectorAll('.pw-swatch[data-colour]').forEach(function (sw) {
      sw.addEventListener('click', function () {
        div.querySelectorAll('.pw-swatch').forEach(function (e) { e.classList.remove('is-active'); });
        sw.classList.add('is-active');
        line.colour = sw.dataset.colour;
        var newImg = (line.style.ci && line.style.ci[line.colour]) || line.style.img;
        if (newImg) div.querySelector('.pw-line-thumb').innerHTML = '<img src="' + escapeAttr(newImg) + '" alt="" />';
      });
    });
    wireSwatchSearch(div);

    div.querySelectorAll('.pw-method-chip[data-deco-idx]').forEach(function (c) {
      c.addEventListener('click', function () {
        div.querySelectorAll('.pw-method-chip').forEach(function (e) { e.classList.remove('is-active'); });
        c.classList.add('is-active');
        line.deco_idx = parseInt(c.dataset.decoIdx, 10);
        var dos = (line.style.do || []).filter(function (d) { return d.type === 'DO'; });
        line.deco_method = dos[line.deco_idx] ? dos[line.deco_idx].method : '';
        recalcAndRenderHardGoods(div, line);
        updateTotals();
      });
    });

    var qtyInput = div.querySelector('[data-control="qty"]');
    qtyInput.addEventListener('input', function () {
      line.qty = Math.max(1, parseInt(qtyInput.value, 10) || 1);
      recalcAndRenderHardGoods(div, line);
      updateTotals();
    });
    div.querySelectorAll('[data-qty]').forEach(function (b) {
      b.addEventListener('click', function () {
        var d = parseInt(b.dataset.qty, 10);
        line.qty = Math.max(1, line.qty + d);
        qtyInput.value = line.qty;
        recalcAndRenderHardGoods(div, line);
        updateTotals();
      });
    });

    var fileInput = div.querySelector('[data-control="artwork"]');
    if (fileInput) wireArtworkUpload(div, line);
  }

  function removeLine(id) {
    Lines = Lines.filter(function (l) { return l.id !== id; });
    renderLines();
  }


  /* ════════════════════════════════════════════════════════════════════
     RECALC + RENDER SUBTOTAL — FIX 7: tier ladder visible
     ════════════════════════════════════════════════════════════════════ */
  function renderTierLadderApparel(div, line, currentTier) {
    var ladder = div.querySelector('[data-tier-ladder]');
    if (!ladder) return;
    var tiers = P.APPAREL_TIERS;
    var prices = line.style.ap_p || {};
    ladder.innerHTML = tiers.map(function (t) {
      var price = prices[t.key];
      if (price == null) return '';
      var active = (t.key === currentTier.key) ? ' is-active' : '';
      var basePrice = prices[tiers[0].key] || price;
      var saving = (basePrice > 0 && t.key !== tiers[0].key)
        ? Math.round(((basePrice - price) / basePrice) * 100)
        : 0;
      return '<div class="pw-tier-step' + active + '">'
           +   '<span class="pw-tier-step-qty">' + t.label + ' units</span>'
           +   '<span class="pw-tier-step-price">' + P.formatAUD(price) + '</span>'
           +   (saving > 0 ? '<span class="pw-tier-step-saving">save ' + saving + '%</span>' : '')
           + '</div>';
    }).join('');
  }

  function renderTierLadderHG(div, line, currentTier) {
    var ladder = div.querySelector('[data-tier-ladder]');
    if (!ladder) return;
    var tiers = line.style.hg_p || [];
    if (tiers.length === 0) { ladder.innerHTML = ''; return; }
    var basePrice = tiers[0].sell || 0;
    ladder.innerHTML = tiers.map(function (t) {
      var active = (currentTier && t.qty === currentTier.tier_qty) ? ' is-active' : '';
      var saving = (basePrice > 0 && t.sell < basePrice)
        ? Math.round(((basePrice - t.sell) / basePrice) * 100)
        : 0;
      return '<div class="pw-tier-step' + active + '">'
           +   '<span class="pw-tier-step-qty">' + t.qty + '+ units</span>'
           +   '<span class="pw-tier-step-price">' + P.formatAUD(t.sell) + '</span>'
           +   (saving > 0 ? '<span class="pw-tier-step-saving">save ' + saving + '%</span>' : '')
           + '</div>';
    }).join('');
  }

  function recalcAndRenderApparel(div, line) {
    var calc = P.calculateApparelLine({
      tierPrices:     line.style.ap_p || {},
      size_breakdown: line.size_mode === 'hard' ? line.size_breakdown : null,
      soft_qty:       line.size_mode === 'soft' ? line.soft_qty : 0,
      product_moq:    line.style.moq || 0,
      deco:           line.deco
    });
    line.calc = calc;

    renderTierLadderApparel(div, line, calc.tier);

    var sub = div.querySelector('[data-subtotal]');
    if (!sub) return;

    var moqWarn = (!calc.moq_ok && calc.total_qty > 0)
      ? '<div class="pw-moq-warn">Quantity is below MOQ of ' + calc.moq + '. Pricing shown but cannot proceed below this minimum.</div>'
      : '';

    var key = P.methodLabelToKey(line.deco.method);
    var screenPrintNote = (line.deco.underbase && key === 'screen-printing') ? ' inc. underbase' : '';
    /* DTG/DTF: rate is per-garment all-in (already covers both sides for the
     * 2-side variant), so don't show "× 2 sides" on the deco line — it'd imply
     * the wrong subtotal. SP/Em DO scale linearly with sides, so show it there.
     * Setup × 2 sides is correct for SP/Em (per-side screens). */
    var isDtgDtf = (key === 'dtg' || key === 'dtf');
    var decoSidesNote  = (calc.sides > 1 && !isDtgDtf) ? ' × 2 sides' : '';
    var decoSidesLabel = (calc.sides > 1 &&  isDtgDtf) ? ' (front + back)' : '';
    var setupSidesNote = (calc.sides > 1 && !isDtgDtf) ? ' × 2 sides' : '';

    sub.innerHTML = ''
      + '<div class="pw-subtotal-row"><span>Garment · ' + calc.total_qty + ' × ' + P.formatAUD(calc.unit_garment_price) + ' (tier ' + calc.tier.label + ')</span><span>' + P.formatAUD(calc.garment_subtotal) + '</span></div>'
      + (calc.deco_rate > 0
          ? '<div class="pw-subtotal-row"><span>' + escapeHtml(line.deco.method) + decoSidesLabel + ' · ' + P.formatAUD(calc.deco_rate) + ' × ' + calc.total_qty + decoSidesNote + '</span><span>' + P.formatAUD(calc.deco_subtotal) + '</span></div>'
          : '')
      + (calc.setup > 0
          ? '<div class="pw-subtotal-row"><span>Setup' + (line.deco.repeat ? ' (repeat)' : '') + screenPrintNote + setupSidesNote + '</span><span>' + P.formatAUD(calc.setup) + '</span></div>'
          : '')
      + '<div class="pw-subtotal-row pw-total"><span>Item subtotal (ex GST)</span><span>' + P.formatAUD(calc.line_total) + '</span></div>'
      + moqWarn;
  }

  function recalcAndRenderHardGoods(div, line) {
    var dos = (line.style.do || []).filter(function (d) { return d.type === 'DO'; });
    var deco = dos[line.deco_idx] || dos[0];

    var calc = P.calculateHardGoodsLine({
      priceTiers:  line.style.hg_p || [],
      decoOption:  deco,
      qty:         line.qty,
      product_moq: line.style.moq || 0
    });
    line.calc = calc;

    renderTierLadderHG(div, line, calc.tier);

    var sub = div.querySelector('[data-subtotal]');
    if (!sub) return;

    var moqWarn = (!calc.moq_ok && calc.qty > 0)
      ? '<div class="pw-moq-warn">Quantity is below MOQ of ' + calc.moq + '. Pricing shown but cannot proceed below this minimum.</div>'
      : '';

    sub.innerHTML = ''
      + '<div class="pw-subtotal-row"><span>Product · ' + calc.qty + ' × ' + P.formatAUD(calc.unit_sell) + (calc.tier ? ' (tier ' + calc.tier.tier_qty + '+)' : '') + '</span><span>' + P.formatAUD(calc.garment_subtotal) + '</span></div>'
      + (deco
          ? '<div class="pw-subtotal-row"><span>' + escapeHtml(deco.method) + ' · ' + P.formatAUD(calc.deco_unit_sell) + ' × ' + calc.qty + '</span><span>' + P.formatAUD(calc.deco_subtotal) + '</span></div>'
          : '')
      + (calc.setup > 0
          ? '<div class="pw-subtotal-row"><span>Setup</span><span>' + P.formatAUD(calc.setup) + '</span></div>'
          : '')
      + '<div class="pw-subtotal-row pw-total"><span>Item subtotal (ex GST)</span><span>' + P.formatAUD(calc.line_total) + '</span></div>'
      + moqWarn;
  }


  /* ════════════════════════════════════════════════════════════════════
     TOTALS
     ════════════════════════════════════════════════════════════════════ */
  function updateTotals() {
    var subtotal = 0, units = 0;
    Lines.forEach(function (line) {
      if (line.calc) subtotal += line.calc.line_total;
      if (line.kind === 'apparel') units += (line.calc ? line.calc.total_qty : 0);
      else units += (line.calc ? line.calc.qty : 0);
    });
    var gst = P.round2(subtotal * 0.10);
    var grand = P.round2(subtotal + gst);

    pw('total-subtotal').textContent = P.formatAUD(subtotal);
    pw('total-gst').textContent = P.formatAUD(gst);
    pw('total-grand').textContent = P.formatAUD(grand);
    pw('line-count').textContent = Lines.length;
    pw('unit-count').textContent = units;
  }


  /* ════════════════════════════════════════════════════════════════════
     ACTIONS — Worker stubs (replace toasts with real fetch calls when ready)
     ════════════════════════════════════════════════════════════════════ */
  function buildPayload(intent) {
    return {
      quote_id: pw('quote-id').textContent,
      intent:   intent,
      line_items: Lines.map(function (l) {
        /* Use the colour-specific image if we have one, otherwise the lead image. */
        var imageUrl = (l.colour && l.style.ci && l.style.ci[l.colour])
                       || l.style.img
                       || '';
        return {
          product_id:    l.style_id,
          handle:        l.style.h,
          title:         l.style.t,
          vendor:        l.style.v,
          image_url:     imageUrl,
          type:          l.kind,
          colour:        l.colour,
          qty:           l.kind === 'apparel'
                         ? (l.calc ? l.calc.total_qty : 0)
                         : l.qty,
          size_breakdown: l.kind === 'apparel' ? l.size_breakdown : null,
          size_mode:     l.kind === 'apparel' ? l.size_mode : null,
          decoration:    l.kind === 'apparel'
                         ? l.deco
                         : { method: l.deco_method, deco_idx: l.deco_idx },
          artwork_filename:       l.kind === 'hg' && l.artwork ? l.artwork.name : null,
          artwork_filename_front: l.kind === 'apparel' && l.artwork && l.artwork.front ? l.artwork.front.name : null,
          artwork_filename_back:  l.kind === 'apparel' && l.artwork && l.artwork.back  ? l.artwork.back.name  : null,
          pricing_breakdown: l.calc
        };
      }),
      totals: {
        subtotal_ex_gst: parseFloat((pw('total-subtotal').textContent || '$0').replace(/[^0-9.]/g, '')),
        gst:             parseFloat((pw('total-gst').textContent || '$0').replace(/[^0-9.]/g, '')),
        total_inc_gst:   parseFloat((pw('total-grand').textContent || '$0').replace(/[^0-9.]/g, ''))
      },
      pricing_engine_version: P.VERSION
    };
  }

  /* Stub: POST to Worker. If WORKER_BASE not configured, just toasts. */
  function postToWorker(endpoint, payload) {
    if (!WORKER_BASE) {
      console.log('[pw-quote-builder] would POST to ' + endpoint, payload);
      return Promise.resolve({ ok: false, mocked: true });
    }
    return fetch(WORKER_BASE + endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (r) { return r.json(); });
  }

  pw('add-line').addEventListener('click', function () {
    pw('search-input').focus();
    showToast('Search above or hit Browse all to add another product');
  });

  pw('checkout-btn').addEventListener('click', function () {
    if (Lines.length === 0) { showToast('Add a product first'); return; }
    if (Lines.some(function (l) { return l.calc && !l.calc.moq_ok; })) {
      showToast('One or more items below MOQ — adjust before checkout'); return;
    }
    var payload = buildPayload('checkout_now');
    showToast('Creating draft order…');
    postToWorker('/draft-order', payload).then(function (res) {
      if (res && res.invoice_url) {
        showToast('Redirecting to invoice…');
        setTimeout(function () { window.location.href = res.invoice_url; }, 600);
      } else if (res && res.mocked) {
        showToast('Worker not configured — payload logged to console');
      } else if (res && res.error) {
        console.error('[checkout]', res);
        showToast('Checkout error: ' + res.error);
      } else {
        showToast('Checkout error — please try again');
      }
    }).catch(function (err) {
      console.error('[checkout]', err);
      showToast('Checkout failed — please try again');
    });
  });

  pw('save-btn').addEventListener('click', function () {
    if (Lines.length === 0) { showToast('Nothing to save yet'); return; }
    var payload = buildPayload('save_only');
    showToast('Saving…');
    postToWorker('/quote/save', payload).then(function (res) {
      if (res && res.magic_link) {
        copyToClipboard(res.magic_link);
        showLinkModal('Quote saved', res.magic_link, 'Magic link copied. Paste it anywhere to reload your quote.');
      } else if (res && res.mocked) {
        showToast('Worker not configured — payload logged to console');
      } else {
        showToast('Save failed: ' + ((res && res.error) || 'unknown error'));
      }
    }).catch(function (err) {
      console.error('[save]', err);
      showToast('Save failed');
    });
  });

  pw('email-btn').addEventListener('click', function () {
    if (Lines.length === 0) { showToast('Nothing to email yet'); return; }
    /* v1: email send not wired — buyer can manually copy the magic link from Save. */
    showToast('Use Save my quote — copy the link, paste into your own email');
  });

  pw('share-btn').addEventListener('click', function () {
    if (Lines.length === 0) { showToast('Nothing to share yet'); return; }
    var payload = buildPayload('share');
    showToast('Generating share link…');
    postToWorker('/quote/share', payload).then(function (res) {
      if (res && res.share_link) {
        copyToClipboard(res.share_link);
        showLinkModal('Share link ready', res.share_link, 'Anyone with this link can view your quote.');
      } else if (res && res.mocked) {
        showToast('Worker not configured — payload logged to console');
      } else {
        showToast('Share failed: ' + ((res && res.error) || 'unknown error'));
      }
    }).catch(function (err) {
      console.error('[share]', err);
      showToast('Share failed');
    });
  });

  pw('pdf-btn').addEventListener('click', function () {
    if (Lines.length === 0) { showToast('Nothing to PDF yet'); return; }
    if (!WORKER_BASE) {
      showToast('Worker not configured');
      console.warn('[pdf] WORKER_BASE not set');
      return;
    }

    var btn = pw('pdf-btn');
    btn.disabled = true;
    showToast('Saving and generating PDF…');

    /* The PDF flow uses a save → real-URL-download chain:
     *   1) POST /quote/save — store the quote, get a token back
     *   2) Navigate a new tab to GET <worker>/quote/<token>/pdf
     *      The worker's GET endpoint returns the PDF with
     *      Content-Disposition: attachment, so the browser handles the
     *      download natively with a sensible filename.
     *
     * Why not use a blob: URL? Shopify's CSP and/or Chrome's blob handling
     * disregard the <a download> attribute when clicked from script context,
     * which causes the current tab to navigate to the blob URL and fail
     * with "Check Internet connection". A real URL bypasses all that. */
    postToWorker('/quote/save', buildPayload('save_for_pdf')).then(function (saveRes) {
      if (!saveRes || !saveRes.token) {
        if (saveRes && saveRes.mocked) return;
        throw new Error((saveRes && saveRes.error) || 'Save failed — no token returned');
      }

      var pdfUrl = WORKER_BASE + '/quote/' + encodeURIComponent(saveRes.token) + '/pdf';

      /* Open in a new tab. The browser sees Content-Disposition: attachment
       * and shows the OS save dialog with the filename pre-filled. The new
       * tab then closes itself (or stays blank — depends on the browser). */
      var win = window.open(pdfUrl, '_blank');

      if (!win) {
        /* Pop-up blocked. Fall back: show a modal with a real link. The
         * user's click on that link satisfies the popup blocker. */
        showLinkModal(
          'PDF ready',
          pdfUrl,
          'Pop-ups are blocked. Click the link above to download your PDF.'
        );
      } else {
        showToast('PDF download started');
      }
    }).catch(function (err) {
      console.error('[pdf]', err);
      showToast('PDF generation failed: ' + (err.message || 'unknown'));
    }).then(function () {
      btn.disabled = false;
    });
  });

  pw('edit-btn').addEventListener('click', function () {
    showToast('Already in edit mode');
  });


  /* ════════════════════════════════════════════════════════════════════
     HELPERS
     ════════════════════════════════════════════════════════════════════ */
  function escapeHtml(s) {
    if (s == null) return '';
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function escapeAttr(s) {
    if (s == null) return '';
    return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function methodMetaApparel(m) {
    return ({
      'Screen Printing': 'big designs · MOQ 25',
      'Embroidery':      'premium · MOQ 10',
      'DTG':             'photo-quality · MOQ 10',
      'DTF':             'small detail · MOQ 10'
    })[m] || '';
  }
  /**
   * Look up a colour name in the SwatchMap and return its render shape.
   *
   * @param {string} name  Colour name as it appears in the catalogue
   * @returns {{kind: 'hex'|'image', value: string, isLight: boolean}|null}
   */
  function lookupSwatch(name) {
    if (!name) return null;
    var key = String(name).toLowerCase().trim();
    var raw = SwatchMap[key];
    if (!raw) {
      /* Try fuzzy fallback — sometimes catalogue colour is "Black/White"
         and swatch map has just "Black". Match the part before the slash. */
      var slashIdx = key.indexOf('/');
      if (slashIdx > 0) {
        raw = SwatchMap[key.slice(0, slashIdx).trim()];
      }
    }
    if (!raw) return null;

    if (raw.charAt(0) === '#') {
      return { kind: 'hex', value: raw, isLight: isLightHex(raw) };
    }
    /* Image swatch — value is a filename, e.g. "eternity.png".
       Build a CDN URL via Shopify Files. */
    return {
      kind:    'image',
      value:   SHOPIFY_FILES_PREFIX + raw + '?width=88',
      isLight: false
    };
  }

  /* Detect if a hex colour is light enough to need a darker swatch border. */
  function isLightHex(hex) {
    var h = (hex || '').replace('#', '');
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    if (h.length !== 6) return false;
    var r = parseInt(h.slice(0, 2), 16);
    var g = parseInt(h.slice(2, 4), 16);
    var b = parseInt(h.slice(4, 6), 16);
    /* Relative luminance — perceptually weighted */
    var lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return lum > 0.85;
  }

  /**
   * Render a single swatch. PDP-style: filled circle, no chrome, ring on active.
   * Falls back to neutral grey if the name isn't in the map.
   */
  function renderSwatch(c, activeColour) {
    var swatch = lookupSwatch(c);
    var active = (c === activeColour) ? ' is-active' : '';
    var lightClass = '';
    var styleAttr = '';
    if (!swatch) {
      styleAttr = 'background:#cccccc;';
    } else if (swatch.kind === 'image') {
      styleAttr = 'background-image:url(' + escapeAttr(swatch.value) + ');background-size:cover;background-position:center;';
    } else {
      styleAttr = 'background:' + swatch.value + ';';
      if (swatch.isLight) lightClass = ' is-light';
    }
    return '<button type="button" class="pw-swatch' + active + lightClass + '"'
         + ' data-colour="' + escapeAttr(c) + '"'
         + ' data-colour-name="' + escapeAttr(c) + '"'
         + ' aria-label="' + escapeAttr(c) + '">'
         +   '<span class="pw-swatch-dot" style="' + styleAttr + '"></span>'
         + '</button>';
  }

  /**
   * Render the full swatch grid: optional search input above (when the
   * colour list is long enough to benefit), then the dot grid, then a
   * "Selected: X" label.
   *
   * Search is purely client-side filter — adds a class to swatches whose
   * names don't contain the query, hiding them via CSS.
   */
  function renderSwatchGrid(colourways, activeColour, lineId) {
    var dots = colourways.map(function (c) { return renderSwatch(c, activeColour); }).join('');
    var showSearch = colourways.length > 12;
    var searchHtml = '';
    if (showSearch) {
      var searchId = 'pw-cs-' + lineId;
      searchHtml = ''
        + '<div class="pw-swatch-search">'
        +   '<svg class="pw-swatch-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">'
        +     '<circle cx="11" cy="11" r="7"/>'
        +     '<line x1="20" y1="20" x2="16.5" y2="16.5"/>'
        +   '</svg>'
        +   '<input type="text" class="pw-swatch-search-input" id="' + searchId + '" placeholder="Search colours…" data-swatch-search />'
        + '</div>';
    }
    return '<div class="pw-swatch-block">'
         +   searchHtml
         +   '<div class="pw-swatches">' + dots + '</div>'
         + '</div>';
  }

  function renderSelectedColourLabel() { return ''; }  /* deprecated — kept as a no-op for any stale callers */

  /**
   * Wire up the swatch search input for a line, if present. Hides swatches
   * whose colour name doesn't contain the query (case-insensitive).
   */
  function wireSwatchSearch(div) {
    var input = div.querySelector('[data-swatch-search]');
    if (!input) return;
    input.addEventListener('input', function () {
      var q = input.value.toLowerCase().trim();
      div.querySelectorAll('.pw-swatch[data-colour]').forEach(function (sw) {
        var name = (sw.dataset.colour || '').toLowerCase();
        if (!q || name.indexOf(q) !== -1) {
          sw.classList.remove('pw-swatch-filtered');
        } else {
          sw.classList.add('pw-swatch-filtered');
        }
      });
    });
  }

  /**
   * APPAREL artwork — PDP-aligned: Front/Back tabs + dashed-border dropzone.
   * Renders the full block (tabs + dropzone). Wire with wireArtworkSection().
   *
   * line.artwork shape: { front: {name,size}|null, back: {name,size}|null }
   * line.art_active_side: 'front' | 'back' (which tab is shown)
   * Back tab is hidden unless deco.front_back === 'front+back' AND method !== Embroidery.
   */
  function renderArtworkSection(line) {
    var deco = line.deco || {};
    var key  = P.methodLabelToKey(deco.method);
    var isEmb = (key === 'embroidery');
    var twoSides = (deco.front_back === 'front+back') && !isEmb;

    /* Force active side back to 'front' if back is no longer available. */
    if (line.art_active_side === 'back' && !twoSides) line.art_active_side = 'front';
    var active = line.art_active_side || 'front';

    var frontFile = line.artwork && line.artwork.front;
    var backFile  = line.artwork && line.artwork.back;

    var tabs = ''
      + '<div class="pw-art-tabs" role="tablist">'
      +   '<button type="button" class="pw-art-tab' + (active === 'front' ? ' is-active' : '') + (frontFile ? ' has-art' : '') + '"'
      +     ' data-art-tab="front" role="tab" aria-selected="' + (active === 'front') + '">'
      +     'Front' + (frontFile ? '<span class="pw-art-tab-dot" aria-hidden="true"></span>' : '')
      +   '</button>'
      + (twoSides
          ? '<button type="button" class="pw-art-tab' + (active === 'back' ? ' is-active' : '') + (backFile ? ' has-art' : '') + '"'
            + ' data-art-tab="back" role="tab" aria-selected="' + (active === 'back') + '">'
            + 'Back' + (backFile ? '<span class="pw-art-tab-dot" aria-hidden="true"></span>' : '')
            + '</button>'
          : '')
      + '</div>';

    return tabs + renderArtworkDropzone(line, active);
  }

  /**
   * The dashed-border dropzone for the *active* side.
   * Two visual states: idle (big upload icon + label + format hint),
   * filled (filename + remove button).
   */
  function renderArtworkDropzone(line, side) {
    var file = line.artwork && line.artwork[side];
    var sideLabel = side === 'back' ? 'Back' : 'Front';

    if (file) {
      return ''
        + '<div class="pw-art-drop has-file" data-art-drop="' + side + '">'
        +   '<div class="pw-art-drop-filled">'
        +     '<svg class="pw-art-drop-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 13l4 4L19 7"/></svg>'
        +     '<div class="pw-art-drop-fileinfo">'
        +       '<span class="pw-art-drop-name">' + escapeHtml(file.name) + '</span>'
        +       '<span class="pw-art-drop-meta">' + escapeHtml(sideLabel) + ' artwork uploaded</span>'
        +     '</div>'
        +     '<button type="button" class="pw-art-drop-remove" data-art-remove="' + side + '" aria-label="Remove ' + escapeAttr(sideLabel) + ' artwork">×</button>'
        +     '<button type="button" class="pw-art-drop-replace" data-art-replace="' + side + '">Replace</button>'
        +     '<input type="file" accept=".ai,.eps,.pdf,.svg,.png,.jpg,.jpeg" data-art-input="' + side + '" style="display:none;" />'
        +   '</div>'
        + '</div>';
    }

    return ''
      + '<label class="pw-art-drop" data-art-drop="' + side + '">'
      +   '<span class="pw-art-drop-icon">'
      +     '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>'
      +   '</span>'
      +   '<span class="pw-art-drop-label">UPLOAD ' + escapeHtml(sideLabel.toUpperCase()) + ' ARTWORK</span>'
      +   '<span class="pw-art-drop-types">PNG · JPG · AI · SVG · EPS</span>'
      +   '<input type="file" accept=".ai,.eps,.pdf,.svg,.png,.jpg,.jpeg" data-art-input="' + side + '" style="display:none;" />'
      + '</label>';
  }

  /**
   * In-place re-render of just the apparel artwork block. Call this when
   * sides toggle (Front+Back checkbox) so the Back tab appears/disappears,
   * or when the deco method changes (Embroidery hides Back tab).
   */
  function refreshArtworkSection(div, line) {
    var section = div.querySelector('.pw-config-block--artwork');
    if (!section) return;
    section.innerHTML = '<div class="pw-art-section-header">Add Your Logo or Artwork</div>'
                      + renderArtworkSection(line);
    wireArtworkSection(div, line);
  }

  /**
   * Wire the apparel artwork section: tab switching, file change, drag/drop, remove.
   * Re-renders just the section in place so we never blow away handlers on the
   * surrounding line.
   */
  function wireArtworkSection(div, line) {
    var section = div.querySelector('.pw-config-block--artwork');
    if (!section) return;

    function rerender() {
      var headerHtml = '<div class="pw-art-section-header">Add Your Logo or Artwork</div>';
      section.innerHTML = headerHtml + renderArtworkSection(line);
      bind();
    }

    function applyFile(side, f) {
      if (!f) return;
      if (!line.artwork) line.artwork = { front: null, back: null };
      line.artwork[side] = { name: f.name, size: f.size };
      line.art_active_side = side;
      rerender();
    }

    function bind() {
      /* Tab clicks */
      section.querySelectorAll('[data-art-tab]').forEach(function (tab) {
        tab.addEventListener('click', function () {
          line.art_active_side = tab.getAttribute('data-art-tab');
          rerender();
        });
      });

      /* File input change (idle state — input is inside the <label>) */
      var fileInput = section.querySelector('[data-art-input]');
      if (fileInput) {
        fileInput.addEventListener('change', function (e) {
          var side = fileInput.getAttribute('data-art-input');
          applyFile(side, e.target.files && e.target.files[0]);
        });
      }

      /* Drag/drop on the dropzone (idle only — when filled, the wrapper is a div, not a label) */
      var drop = section.querySelector('label.pw-art-drop[data-art-drop]');
      if (drop) {
        drop.addEventListener('dragover', function (e) {
          e.preventDefault();
          drop.classList.add('is-drag-over');
        });
        drop.addEventListener('dragleave', function () {
          drop.classList.remove('is-drag-over');
        });
        drop.addEventListener('drop', function (e) {
          e.preventDefault();
          drop.classList.remove('is-drag-over');
          var side = drop.getAttribute('data-art-drop');
          var f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
          if (f) applyFile(side, f);
        });
      }

      /* Remove */
      var removeBtn = section.querySelector('[data-art-remove]');
      if (removeBtn) {
        removeBtn.addEventListener('click', function () {
          var side = removeBtn.getAttribute('data-art-remove');
          if (line.artwork) line.artwork[side] = null;
          rerender();
        });
      }

      /* Replace — opens the hidden file input */
      var replaceBtn = section.querySelector('[data-art-replace]');
      if (replaceBtn) {
        replaceBtn.addEventListener('click', function () {
          var side = replaceBtn.getAttribute('data-art-replace');
          var input = section.querySelector('[data-art-input="' + side + '"]');
          if (input) input.click();
        });
      }
    }

    bind();
  }

  /**
   * Wire up the artwork upload — file change, drag/drop, in-place re-render.
   * HARD-GOODS only. Single-file shape: line.artwork = {name,size} | null.
   */
  function wireArtworkUpload(div, line) {
    var label = div.querySelector('[data-art-upload]');
    if (!label) return;
    var fileInput = label.querySelector('[data-control="artwork"]');

    function applyFile(f) {
      if (!f) return;
      line.artwork = { name: f.name, size: f.size };
      /* Re-render the upload widget so icon + title + CTA all swap cleanly. */
      var fresh = document.createElement('div');
      fresh.innerHTML = renderArtworkUpload(line).trim();
      label.parentNode.replaceChild(fresh.firstChild, label);
      wireArtworkUpload(div, line); /* re-bind on the new element */
    }

    fileInput.addEventListener('change', function (e) {
      applyFile(e.target.files && e.target.files[0]);
    });

    /* Drag-and-drop */
    label.addEventListener('dragover', function (e) {
      e.preventDefault();
      label.classList.add('is-drag-over');
    });
    label.addEventListener('dragleave', function () {
      label.classList.remove('is-drag-over');
    });
    label.addEventListener('drop', function (e) {
      e.preventDefault();
      label.classList.remove('is-drag-over');
      var f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      if (f) applyFile(f);
    });
  }

  function renderArtworkUpload(line) {
    var hasFile = !!line.artwork;
    var titleHtml, hintHtml, ctaText;
    if (hasFile) {
      titleHtml = '<span class="pw-art-upload-name">' + escapeHtml(line.artwork.name) + '</span>';
      hintHtml  = '<span class="pw-art-upload-hint">Click to replace</span>';
      ctaText   = 'CHANGE';
    } else {
      titleHtml = '<span class="pw-art-upload-title">Upload your artwork</span>';
      hintHtml  = '<span class="pw-art-upload-hint">Vector preferred · .ai .eps .pdf .svg · or .png .jpg over 300 dpi</span>';
      ctaText   = 'UPLOAD';
    }
    return ''
      + '<label class="pw-art-upload' + (hasFile ? ' has-file' : '') + '" data-art-upload>'
      +   '<span class="pw-art-upload-icon">'
      +     (hasFile
            ? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 13l4 4L19 7"/></svg>'
            : '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>')
      +   '</span>'
      +   '<span class="pw-art-upload-body">'
      +     titleHtml
      +     hintHtml
      +   '</span>'
      +   '<span class="pw-art-upload-cta">' + ctaText + '</span>'
      +   '<input type="file" accept=".ai,.eps,.pdf,.svg,.png,.jpg,.jpeg" data-control="artwork" style="display:none;" />'
      + '</label>';
  }

  function showToast(msg) {
    var t = pw('toast');
    t.textContent = msg;
    t.classList.add('is-visible');
    clearTimeout(window.__pwToastTimer);
    window.__pwToastTimer = setTimeout(function () { t.classList.remove('is-visible'); }, 3500);
  }

  /* Clipboard helper — uses Clipboard API with execCommand fallback. */
  function copyToClipboard(text) {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).catch(function () { fallbackCopy(text); });
    } else {
      fallbackCopy(text);
    }
  }
  function fallbackCopy(text) {
    var ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.focus(); ta.select();
    try { document.execCommand('copy'); } catch (e) {}
    document.body.removeChild(ta);
  }

  /* Modal showing a generated link with a copy button. Built on-the-fly so
     we don't need extra Liquid markup. Shares the existing pw-modal styles. */
  function showLinkModal(title, link, helperText) {
    var existing = ROOT.querySelector('[data-pw="link-modal"]');
    if (existing) existing.remove();

    var wrap = document.createElement('div');
    wrap.className = 'pw-modal-backdrop';
    wrap.setAttribute('data-pw', 'link-modal');
    wrap.setAttribute('role', 'dialog');
    wrap.setAttribute('aria-modal', 'true');
    wrap.innerHTML = ''
      + '<div class="pw-modal" style="max-width:520px;">'
      +   '<div class="pw-modal-header">'
      +     '<h2 class="pw-modal-title">' + escapeHtml(title) + '</h2>'
      +     '<button type="button" class="pw-modal-close" data-link-close aria-label="Close">×</button>'
      +   '</div>'
      +   '<div style="padding:22px;">'
      +     (helperText ? '<p style="margin:0 0 14px;font-size:12px;color:var(--pw-mid);line-height:1.5;">' + escapeHtml(helperText) + '</p>' : '')
      +     '<div style="display:flex;gap:0;border:0.5px solid var(--pw-border);border-radius:var(--pw-radius);overflow:hidden;">'
      +       '<input type="text" readonly value="' + escapeAttr(link) + '" style="flex:1;border:none;padding:12px 14px;font-family:var(--pw-font-mono);font-size:12px;background:var(--pw-soft-grey);min-width:0;" data-link-input />'
      +       '<button type="button" data-link-copy style="border:none;background:var(--pw-ink);color:white;padding:0 18px;font-family:var(--pw-font-mono);font-size:11px;text-transform:uppercase;letter-spacing:0.1em;cursor:pointer;">Copy</button>'
      +     '</div>'
      +   '</div>'
      + '</div>';

    ROOT.appendChild(wrap);

    function close() { wrap.remove(); }
    wrap.querySelector('[data-link-close]').addEventListener('click', close);
    wrap.addEventListener('click', function (e) { if (e.target === wrap) close(); });

    wrap.querySelector('[data-link-copy]').addEventListener('click', function () {
      copyToClipboard(link);
      var input = wrap.querySelector('[data-link-input]');
      input.select();
      var btn = wrap.querySelector('[data-link-copy]');
      btn.textContent = 'Copied ✓';
      setTimeout(function () { btn.textContent = 'Copy'; }, 1800);
    });

    /* Auto-select the input field so a hand-keyboard copy works too. */
    setTimeout(function () {
      var input = wrap.querySelector('[data-link-input]');
      if (input) { input.focus(); input.select(); }
    }, 50);
  }

  /* ── Magic-link reload — detect ?token=xyz on load and rehydrate ───── */
  function maybeRehydrate() {
    var params = new URLSearchParams(window.location.search);
    var token = params.get('token');
    if (!token) return;
    if (!WORKER_BASE) {
      showToast('Cannot reload quote: Worker not configured');
      return;
    }

    /* Show a loading state while we fetch */
    pw('loading').textContent = 'Loading saved quote…';
    pw('loading').classList.remove('pw-hidden');
    pw('empty').classList.add('pw-hidden');

    fetch(WORKER_BASE + '/quote/' + encodeURIComponent(token))
      .then(function (r) { return r.json(); })
      .then(function (res) {
        if (!res || !res.ok || !res.quote) {
          showToast('Saved quote not found or expired');
          pw('loading').classList.add('pw-hidden');
          pw('empty').classList.remove('pw-hidden');
          return;
        }
        rehydrateFromQuote(res.quote);
      })
      .catch(function (err) {
        console.error('[rehydrate]', err);
        showToast('Failed to load saved quote');
        pw('loading').classList.add('pw-hidden');
        pw('empty').classList.remove('pw-hidden');
      });
  }

  /**
   * Rebuild Lines[] from a saved quote payload. Each saved line item
   * carries enough information (product_id, decoration, sizes, qty) to
   * fully reconstruct the local state.
   */
  function rehydrateFromQuote(quote) {
    /* Wait until catalogue is loaded — if it isn't yet, retry. */
    if (Catalogue.length === 0) {
      setTimeout(function () { rehydrateFromQuote(quote); }, 200);
      return;
    }

    Lines = [];
    (quote.line_items || []).forEach(function (li) {
      var style = Catalogue.find(function (s) { return s.i === li.product_id; });
      if (!style) {
        console.warn('[rehydrate] product not found in catalogue:', li.product_id);
        return;
      }
      var line = {
        id: uuid(),
        style_id: style.i,
        style:    style,
        colour:   li.colour || (style.cw && style.cw[0]) || '',
        kind:     li.type
      };
      if (li.type === 'apparel') {
        line.size_mode      = li.size_mode || 'hard';
        line.size_breakdown = li.size_breakdown || {};
        line.soft_qty       = li.qty || 50;
        line.deco           = li.decoration || {
          method: 'Embroidery', colours: 1, repeat: false, front_back: 'front', underbase: false
        };
        /* Per-side artwork (new shape). Legacy save files only have `artwork_filename`
         * — treat that as the front file. */
        var frontName = li.artwork_filename_front || li.artwork_filename || null;
        var backName  = li.artwork_filename_back  || null;
        line.artwork = {
          front: frontName ? { name: frontName, size: 0 } : null,
          back:  backName  ? { name: backName,  size: 0 } : null
        };
        line.art_active_side = 'front';
      } else {
        line.artwork     = li.artwork_filename ? { name: li.artwork_filename, size: 0 } : null;
        line.qty         = li.qty || 0;
        line.deco_idx    = (li.decoration && typeof li.decoration.deco_idx === 'number') ? li.decoration.deco_idx : 0;
        line.deco_method = (li.decoration && li.decoration.method) || '';
      }
      Lines.push(line);
    });

    /* Restore the original quote_id label on the page. */
    if (quote.quote_id) pw('quote-id').textContent = quote.quote_id;

    pw('loading').classList.add('pw-hidden');
    renderLines();
    showToast('Loaded saved quote · ' + Lines.length + ' item' + (Lines.length === 1 ? '' : 's'));
  }

  /* Kick off rehydrate after catalogue load. The catalogue load promise
     above also clears the loading state; this hooks in once it's done. */
  var rehydrateInterval = setInterval(function () {
    if (Catalogue.length > 0) {
      clearInterval(rehydrateInterval);
      maybeRehydrate();
    }
  }, 100);
  /* Safety: stop trying after 15s. */
  setTimeout(function () { clearInterval(rehydrateInterval); }, 15000);

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      pw('browse-modal').classList.add('is-hidden');
      pw('search-results').classList.add('is-hidden');
    }
  });

})();
